import React from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import MedicalServicesIcon from '@material-ui/icons/MedicalServices';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import MicIcon from '@material-ui/icons/Mic';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import BiotechIcon from '@material-ui/icons/Biotech';
import ScienceIcon from '@material-ui/icons/Science';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';

const MINT = '#66ffd1';
const GREEN = '#22c38b';
const SOFT_MINT = '#b9ffe9';
const CARD_BG = 'rgba(5, 25, 22, 0.86)';
const PAGE_BG = [
  'radial-gradient(ellipse 120% 66% at 50% -12%, rgba(102,255,209,0.24) 0%, transparent 54%)',
  'radial-gradient(ellipse 68% 56% at 12% 92%, rgba(34,195,139,0.24) 0%, transparent 58%)',
  'linear-gradient(135deg, rgba(3,16,14,1) 0%, rgba(6,31,27,0.98) 44%, rgba(8,46,39,0.96) 78%, rgba(102,255,209,0.22) 100%)',
].join(', ');

const sectionSx = {
  py: { xs: 5, md: 7 },
  borderBottom: '1px solid rgba(102,255,209,0.2)',
};

const cardSx = {
  p: 2.4,
  borderRadius: 2,
  bgcolor: CARD_BG,
  border: '1px solid rgba(102,255,209,0.35)',
  height: '100%',
};

const HOSTS_INVESTMENT = 340;

const productBreakdown = [
  {
    title: 'Cobertura en ExpoSaludPlus',
    detail: 'Cobertura en campo durante los 4 días del evento con entrevistas dinámicas en stands y recorrido por zonas clave.',
    investment: 200,
  },
  {
    title: 'Host y conducción periodística',
    detail: 'Conducción y entrevistas por Yelitza Yánez y Margarita Toledo, aportando credibilidad editorial y presencia de marca.',
    investment: HOSTS_INVESTMENT,
  },
  {
    title: 'Entrevistas para difusión',
    detail: 'Piezas listas para publicar en ecosistema TNO y canales del evento, enfocadas en propuesta de valor de los expositores.',
    investment: 80,
  },
  {
    title: 'Entrega de material en bruto',
    detail: 'Disponibilidad de entrevistas en bruto para el cliente si lo solicita, manteniendo créditos de producción para TNO Network.',
    investment: 40,
  },
  {
    title: 'Amplificación editorial',
    detail: 'Refuerzo con piezas para visibilidad digital y continuidad de comunicación postevento.',
    investment: 60,
  },
];

const BASE_PLAN_INVESTMENT = productBreakdown
  .filter((item) => item.title !== 'Host y conducción periodística')
  .reduce((sum, item) => sum + item.investment, 0);
const TOTAL_INVESTMENT = productBreakdown.reduce((sum, item) => sum + item.investment, 0);

function LandingMediaKitExpoSalud() {
  return (
    <Box
      sx={{
        color: '#ecfff9',
        minHeight: '100vh',
        bgcolor: '#041411',
        backgroundImage: PAGE_BG,
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Container>
        <Box sx={{ ...sectionSx, pt: { xs: 7, md: 9 }, textAlign: 'center' }}>
          <Box
            sx={{
              backgroundImage: 'url(/images/logos/logo_white.PNG)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              height: { xs: 98, md: 128 },
              maxWidth: 380,
              mx: 'auto',
              mb: 2.2,
            }}
          />
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              mx: 'auto',
              mb: 1.6,
              bgcolor: 'rgba(102,255,209,0.12)',
              border: '1px solid rgba(102,255,209,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MedicalServicesIcon sx={{ color: MINT, fontSize: 64 }} />
          </Box>
          <Typography sx={{ color: MINT, fontWeight: 800, letterSpacing: 3, mb: 1.5 }} variant="overline">
            Propuesta Relámpago | ExpoSaludPlus x TNO Network
          </Typography>
          <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.12, mb: 1.6 }}>
            45 Días de Autoridad Médica Total
          </Typography>
          <Typography sx={{ color: SOFT_MINT, fontWeight: 700, fontSize: { xs: '1.08rem', md: '1.25rem' }, mb: 2 }}>
            Cobertura integral con hosts en Expo para convertir expositores en activos digitales permanentes
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" gap={1.4} mb={2.1}>
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: '50%',
                bgcolor: 'rgba(102,255,209,0.16)',
                border: '1px solid rgba(102,255,209,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BiotechIcon sx={{ color: MINT, fontSize: 34 }} />
            </Box>
            <Typography sx={{ color: MINT, fontWeight: 800, fontSize: '1.15rem' }}>+</Typography>
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: '50%',
                bgcolor: 'rgba(102,255,209,0.16)',
                border: '1px solid rgba(102,255,209,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ScienceIcon sx={{ color: MINT, fontSize: 34 }} />
            </Box>
          </Box>
          <Typography
            sx={{
              maxWidth: 980,
              mx: 'auto',
              color: 'rgba(236,255,249,0.9)',
              lineHeight: 1.7,
              fontSize: { xs: '1.02rem', md: '1.1rem' },
            }}
          >
            Mantenemos el plan base y sumamos hosts de trayectoria: <strong>$380 + $340</strong> para un total de <strong>$720 USD</strong> por los 4
            días del evento, activando cobertura en campo, entrevistas y contenido de alto impacto para cada expositor.
          </Typography>
        </Box>

        <Box sx={sectionSx}>
          <Box
            sx={{
              py: 4,
              px: 3,
              borderRadius: 2,
              bgcolor: 'rgba(8, 46, 39, 0.72)',
              borderLeft: 4,
              borderColor: GREEN,
              border: '1px solid rgba(102,255,209,0.3)',
            }}
          >
            <Typography
              sx={{ textTransform: 'uppercase', fontWeight: 700 }}
              variant="overline"
              color="inherit"
              display="block"
              gutterBottom
            >
              Propuesta de valor agregado
            </Typography>
            <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: MINT }}>
              Cuatro días de evento, 45 días de visibilidad para sus aliados
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(236,255,249,0.92)' }} paragraph>
              Transformamos un patrocinio de 4 días en una campaña estratégica de 45 días.
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(236,255,249,0.92)' }} paragraph>
              Con TNO Network, ExpoSaludPlus no se vive solo en el recinto. La experiencia comienza antes de abrir puertas y sigue dejando huella
              mucho después del evento.
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(236,255,249,0.92)' }} paragraph>
              Esta alianza le da a los organizadores una propuesta mucho más poderosa para patrocinadores y marcas: no solo presencia física en feria,
              sino una visibilidad emocional y sostenida dentro del ecosistema TNO.
            </Typography>

            <Box sx={{ '& > div': { mb: 2 } }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: MINT }} fontWeight={600} gutterBottom>
                  Antes del evento
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(236,255,249,0.92)' }}>
                  Activamos entrevistas previas en estudio para presentar a organizadores, patrocinadores y voceros del bienestar, la prevención y la
                  innovación médica con un tono humano y profesional.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ color: MINT }} fontWeight={600} gutterBottom>
                  Durante los 4 días
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(236,255,249,0.92)', mb: 1 }}>
                  La cobertura se expande a través de toda nuestra arquitectura:
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 0 }}>
                  <Typography component="li" variant="body2" sx={{ color: 'rgba(236,255,249,0.92)' }}>
                    <strong>Señal TNO:</strong> rotativa y presencia constante del evento.
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ color: 'rgba(236,255,249,0.92)' }}>
                    <strong>Cobertura en campo:</strong> entrevistas y recorrido liderados por Yelitza Yánez y Margarita Toledo.
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ color: 'rgba(236,255,249,0.92)' }}>
                    <strong>TNO Digest y clips sociales:</strong> historias que extienden el alcance de la Expo y de sus patrocinadores.
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ color: MINT }} fontWeight={600} gutterBottom>
                  Después del evento
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(236,255,249,0.92)' }}>
                  Los momentos más valiosos quedan disponibles On-Demand para que la comunidad los consulte y las marcas capitalicen la conversación
                  mucho más allá del cierre.
                </Typography>
              </Box>
            </Box>

            <Typography variant="subtitle2" fontWeight={600} sx={{ color: MINT, mt: 3, mb: 1 }}>
              El beneficio para el organizador
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(236,255,249,0.92)' }}>
              El resultado es una Expo con mayor valor percibido, patrocinadores mejor respaldados y una narrativa de marca que combina cercanía,
              rigor y profesionalismo.
            </Typography>
          </Box>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.3 }}>
            <LocalHospitalIcon sx={{ color: GREEN }} />
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.95rem' } }}>
              1. Cobertura Dual: Sede + Expo
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={cardSx}>
                <Typography sx={{ color: MINT, fontWeight: 800, mb: 1 }}>En la Expo (C.C. Líder)</Typography>
                <Typography sx={{ color: 'rgba(236,255,249,0.88)', lineHeight: 1.6 }}>
                  Cobertura y entrevistas dinámicas en stand durante los 4 días para capturar innovación en tiempo real y mostrar la propuesta de
                  valor de cada marca.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={cardSx}>
                <Typography sx={{ color: MINT, fontWeight: 800, mb: 1 }}>Hosts de Cobertura</Typography>
                <Typography sx={{ color: 'rgba(236,255,249,0.88)', lineHeight: 1.6 }}>
                  La cobertura en ExpoSaludPlus estará liderada por las reconocidas periodistas <strong>Yelitza Yánez</strong> y{' '}
                  <strong>Margarita Toledo</strong>, dos voces de amplia trayectoria y credibilidad, con presencia en campo, conducción de entrevistas
                  y un estilo cercano que eleva la experiencia de cada expositor.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.3 }}>
            <BusinessCenterIcon sx={{ color: GREEN }} />
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.95rem' } }}>
              2. Gira de Expositores por Programa Especializado
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {[
              {
                title: 'TNO Business',
                text: 'Clínicas, laboratorios y tecnología médica con enfoque B2B y retorno comercial.',
              },
              {
                title: 'En Sintonía con tu Salud',
                text: 'Especialistas médicos, prevención y bienestar integral con voz experta.',
              },
              {
                title: 'Desde la Cumbre',
                text: 'Suministros, ciencia y resiliencia médica para marcas técnicas del sector.',
              },
              {
                title: 'La Dosis Positiva',
                text: 'Salud mental, bienestar emocional y estética con narrativa cercana y aspiracional.',
              },
              {
                title: 'Somos Padres',
                text: 'Maternidad y paternidad consciente: acompañamiento experto para marcas y servicios enfocados en el bienestar familiar.',
              },
              {
                title: 'Punto de Fe',
                text: 'Salud mental y espiritual con mensajes de esperanza, propósito y equilibrio interior para audiencias que buscan bienestar integral.',
              },
            ].map((item) => (
              <Grid item xs={12} md={6} key={item.title}>
                <Box sx={cardSx}>
                  <Typography sx={{ color: MINT, fontWeight: 800, mb: 1 }}>{item.title}</Typography>
                  <Typography sx={{ color: 'rgba(236,255,249,0.88)', lineHeight: 1.58 }}>{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.3 }}>
            <AutorenewIcon sx={{ color: GREEN }} />
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.95rem' } }}>
              3. Refuerzo Post-Evento y SEO
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={cardSx}>
                <Typography sx={{ color: MINT, fontWeight: 800, mb: 1 }}>TNO Digest especializado</Typography>
                <Typography sx={{ color: 'rgba(236,255,249,0.88)', lineHeight: 1.6 }}>
                  Redacción editorial optimizada para Google, construyendo autoridad digital para cada expositor y extendiendo visibilidad orgánica.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={cardSx}>
                <Typography sx={{ color: MINT, fontWeight: 800, mb: 1 }}>Rotativa 24/7</Typography>
                <Typography sx={{ color: 'rgba(236,255,249,0.88)', lineHeight: 1.6 }}>
                  12 spots diarios durante 45 días para mantener a ExpoSaludPlus y sus aliados en top of mind local e internacional.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={sectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.3 }}>
            <MicIcon sx={{ color: GREEN }} />
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.95rem' } }}>
              4. Desglose de Productos
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {productBreakdown.map((item) => (
              <Grid item xs={12} md={6} key={item.title}>
                <Box sx={cardSx}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={0.8}>
                    <Typography sx={{ color: MINT, fontWeight: 800 }}>{item.title}</Typography>
                    <Typography sx={{ color: SOFT_MINT, fontWeight: 800 }}>${item.investment}</Typography>
                  </Box>
                  <Typography sx={{ color: 'rgba(236,255,249,0.93)', lineHeight: 1.6 }}>{item.detail}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ ...cardSx, mt: 2.2 }}>
            <Typography sx={{ color: MINT, fontWeight: 800, mb: 1.2 }}>Tabla de inversión (Item / Precio)</Typography>
            <Box sx={{ border: '1px solid rgba(102,255,209,0.35)', borderRadius: 1.5, overflow: 'hidden' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1.4fr 1fr', md: '2fr 1fr' },
                  bgcolor: 'rgba(102,255,209,0.16)',
                  px: 1.4,
                  py: 1,
                }}
              >
                <Typography sx={{ fontWeight: 800, color: MINT }}>Item</Typography>
                <Typography sx={{ fontWeight: 800, color: MINT, textAlign: 'right' }}>Precio</Typography>
              </Box>
              {[
                ...productBreakdown.map((item) => ({
                  item: item.title,
                  price: `$${item.investment}`,
                })),
                { item: 'Subtotal plan base (sin hosts)', price: `$${BASE_PLAN_INVESTMENT}` },
                { item: 'Total inversión (4 días)', price: `$${TOTAL_INVESTMENT}` },
              ].map((row) => {
                const isTotalRow = row.item === 'Total inversión (4 días)';
                return (
                  <Box
                    key={row.item}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1.4fr 1fr', md: '2fr 1fr' },
                      px: 1.4,
                      py: 1,
                      borderTop: '1px solid rgba(102,255,209,0.2)',
                      bgcolor: isTotalRow ? 'rgba(102,255,209,0.12)' : 'rgba(255,255,255,0.01)',
                    }}
                  >
                    <Typography
                      sx={{
                        color: isTotalRow ? MINT : 'rgba(236,255,249,0.93)',
                        fontWeight: isTotalRow ? 900 : 500,
                        fontSize: isTotalRow ? { xs: '1.18rem', md: '1.28rem' } : 'inherit',
                      }}
                    >
                      {row.item}
                    </Typography>
                    <Typography
                      sx={{
                        color: isTotalRow ? '#ffffff' : SOFT_MINT,
                        textAlign: 'right',
                        fontWeight: isTotalRow ? 900 : 700,
                        fontSize: isTotalRow ? { xs: '1.75rem', md: '2.05rem' } : 'inherit',
                        lineHeight: isTotalRow ? 1.2 : 'inherit',
                      }}
                    >
                      {row.price}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...sectionSx, borderBottom: 'none', pb: { xs: 8, md: 10 } }}>
          <Grid container spacing={2.2} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography sx={{ color: MINT, fontWeight: 800, mb: 1 }} variant="overline">
                  Inversión Única
                </Typography>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, mb: 1 }}>${TOTAL_INVESTMENT} USD / 4 días</Typography>
                <Typography sx={{ color: 'rgba(236,255,249,0.88)', lineHeight: 1.62 }}>
                  Desglose de inversión: plan base de cobertura y difusión <strong>${BASE_PLAN_INVESTMENT}</strong> + hosts periodísticos de
                  trayectoria <strong>${HOSTS_INVESTMENT}</strong>. Incluye opción de entrega de material en bruto cuando se solicite, con créditos
                  correspondientes.
                </Typography>
                <Typography sx={{ color: SOFT_MINT, fontWeight: 700, mt: 1.2 }}>Cobertura total: 4 días de ExpoSaludPlus.</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <TrendingUpIcon sx={{ color: MINT, mb: 1 }} />
                <Typography sx={{ color: MINT, fontWeight: 800, mb: 1.1 }}>Por qué esta oferta cierra rápido</Typography>
                <Typography sx={{ color: 'rgba(236,255,249,0.88)', lineHeight: 1.58 }}>
                  Costo-beneficio superior, vocería de autoridad médica real y permanencia SEO: la combinación ideal para ayudar al equipo comercial a
                  captar y retener expositores.
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Typography sx={{ textAlign: 'center', color: SOFT_MINT, mt: 3, fontStyle: 'italic', fontWeight: 700 }}>
            "ExpoSaludPlus no termina al cerrar puertas: se transforma en presencia digital de alto rendimiento."
          </Typography>
          <PriceVatDisclaimer sx={{ mt: 2, color: 'rgba(236,255,249,0.75)' }} />
        </Box>
      </Container>
    </Box>
  );
}

export default LandingMediaKitExpoSalud;
